// Flashcards - build study decks and drill them with a flip-card study mode.
// Everything is stored locally (localStorage). A bulk "paste import" turns a
// pasted term/definition list, a Quizlet export or an Anki text export into a deck in one step, and study
// mode shuffles, flips, and self-grades so a student can track what they know.
import { el, btn, input, ta, sel, field, wrap, note, announce, saveFlag } from "./ui.js";
import { parseImport, previewImport, uid, repairCardIds } from "./flashcards-import.js";
import { readJSON, writeJSON } from "../shared/store.js";
import { bump } from "../shared/activity.js";

const KEY = "revault:flashcards";

const load = () => {
	const v = readJSON(KEY, null);
	if (v && Array.isArray(v.decks)) {
		if (repairCardIds(v)) writeJSON(KEY, v);
		return v;
	}
	return { decks: [], active: null };
};

// Study Helper hand-off: add reviewed cards to a deck (by id, or by name; a new name makes a new
// deck) in the same store and shape as a manual add. Returns { deckId, name, added }.
export function listDecks() { return load().decks.map((d) => ({ id: d.id, name: d.name, count: d.cards.length })); }
export function addCards(deckNameOrId, cards) {
	const db = load();
	const clean = (Array.isArray(cards) ? cards : []).map((c) => ({ front: String(c.front || "").trim().slice(0, 500), back: String(c.back || "").trim().slice(0, 500) })).filter((c) => c.front && c.back);
	let deck = db.decks.find((d) => d.id === deckNameOrId) || db.decks.find((d) => d.name === String(deckNameOrId || "").trim());
	if (!deck) { deck = { id: uid(), name: String(deckNameOrId || "").trim().slice(0, 80) || "Study Helper cards", cards: [] }; db.decks.unshift(deck); }
	for (const c of clean) deck.cards.push({ id: uid(), front: c.front, back: c.back });
	db.active = deck.id;
	writeJSON(KEY, db);
	return { deckId: deck.id, name: deck.name, added: clean.length };
}

// Carry codes live in Settings. They're only offered while the server has them on: the app's
// presence beacon notes that under rv:carry, since this page has no beacon of its own.
const carryOn = () => { try { return localStorage.getItem("rv:carry") === "1"; } catch { return false; } };
const signedIn = () => { try { const j = JSON.parse(localStorage.getItem("revault:account") || "null"); return !!(j && j.token); } catch { return false; } };
function openCarry() {
	const url = "/settings#set=carryCode";
	if (window.parent && window.parent !== window) { try { window.parent.postMessage({ type: "revault-open", url }, location.origin); return; } catch { /* open it here instead */ } }
	location.href = url;
}
const carryLink = (text) => btn(text, openCarry, "rv-fc-carry");

// Paste formats. Auto-detect is the free-form list; the others are explicit choices.
const PRESETS = [
	{ id: "auto", label: "Auto-detect", sample: "One card per line, e.g.\nphotosynthesis - how plants make food\nmitosis: cell division\nOsmosis\twater diffusion",
		help: ["One card per line. Separate the term and definition with a Tab, dash, colon, or equals sign."] },
	{ id: "quizlet", label: "Quizlet export", sample: "Paste the text from Quizlet's Export box, e.g.\nmitosis\tcell division\nosmosis\twater moving through a membrane",
		help: ["In Quizlet, open your set, choose the ... menu, then Export. Copy the text and paste it here.",
			"Pick the same separators you chose in Quizlet. Tab and New line are Quizlet's defaults.",
			"Paste the text itself. A link to the set won't work here."] },
	{ id: "anki", label: "Anki (Notes in Plain Text)", sample: "Paste the text of an Anki export, e.g.\n#separator:tab\n#html:true\nmitosis\tcell division<br>makes two cells",
		help: ["In Anki, choose File, then Export. Pick \"Notes in Plain Text\", export, open the .txt file, then copy everything and paste it here.",
			"Formatting is turned into plain text and images or sounds are left out. The first field becomes the front, the second the back."] },
];
// Remembered while the page is open, so a re-render keeps the chosen format.
const importOpts = { open: false, preset: "auto", term: "tab", termCustom: "", row: "newline", rowCustom: "" };

export function flashcards(root) {
	let db = load();
	// _studying is screen state, never stored (a reload opens the deck list).
	const flag = saveFlag(); // kept across re-renders
	const save = () => flag.set(writeJSON(KEY, Object.assign({}, db, { decks: db.decks.map((d) => { const c = Object.assign({}, d); delete c._studying; return c; }) })));
	const deckById = (id) => db.decks.find((d) => d.id === id);

	// Every change re-renders the tool, so put focus back on a sensible control afterwards.
	const refocus = (sel) => { const t = root.querySelector(sel); if (t) t.focus(); return t; };
	function render() {
		const active = deckById(db.active);
		if (active && active._studying) return renderStudy(active);
		renderManage(active);
	}

	// --- deck list + editor --------------------------------------------------
	function renderManage(active) {
		const s = wrap(root); s.classList.add("rv-fc");
		const aside = el("div", "rv-fc-decks");
		const head = el("div", "rv-fc-decks-head");
		head.append(el("span", "rv-fc-decks-title", "Decks"), btn("+ New", newDeck, "rv-tbtn rv-tbtn-accent"));
		aside.appendChild(head);
		if (!db.decks.length) {
			aside.appendChild(note("No decks yet. Create one to start."));
			if (carryOn() && !signedIn()) aside.appendChild(carryLink("Used Re:Vault on another device? Enter a carry code."));
		}
		db.decks.forEach((d) => {
			const item = el("button", "rv-fc-deck" + (d.id === db.active ? " active" : ""));
			item.type = "button";
			if (d.id === db.active) item.setAttribute("aria-current", "true");
			const label = el("span", "rv-fc-deck-name", d.name);
			const count = el("span", "rv-fc-deck-count", String(d.cards.length));
			count.setAttribute("aria-label", d.cards.length + (d.cards.length === 1 ? " card" : " cards"));
			item.append(label, count);
			item.addEventListener("click", () => { db.active = d.id; render(); const t = root.querySelector(".rv-fc-title"); if (t) t.focus(); });
			aside.appendChild(item);
		});
		if (db.decks.length && carryOn()) aside.appendChild(carryLink("Take my cards with me"));

		const main = el("div", "rv-fc-main");
		main.appendChild(flag);
		if (!active) {
			main.appendChild(note("Select a deck on the left, or create a new one."));
			s.append(aside, main); return;
		}

		// deck toolbar
		const bar = el("div", "rv-fc-bar");
		const title = input("text", { value: active.name });
		title.classList.add("rv-fc-title"); title.setAttribute("aria-label", "Deck name");
		title.addEventListener("input", () => { active.name = title.value.trim() || "Untitled deck"; save(); const li = aside.querySelector(".rv-fc-deck.active .rv-fc-deck-name"); if (li) li.textContent = active.name; });
		const study = btn("▶ Study", () => { if (!active.cards.length) return; active._studying = true; active.studiedAt = Date.now(); save(); render(); }, "rv-tbtn rv-tbtn-accent");
		if (!active.cards.length) { study.disabled = true; study.title = "Add cards first"; }
		const del = btn("Delete deck", () => { if (!confirm("Delete \"" + active.name + "\" and its cards?")) return; db.decks = db.decks.filter((x) => x.id !== active.id); db.active = db.decks[0] ? db.decks[0].id : null; save(); render(); announce("Deck deleted"); refocus(".rv-fc-deck.active") || refocus(".rv-fc-decks-head button"); }, "rv-tbtn rv-tbtn-danger");
		bar.append(title, study, del);
		main.appendChild(bar);

		// add-card row
		const add = el("form", "rv-fc-add");
		const front = input("text", { placeholder: "Term / question" });
		const back = input("text", { placeholder: "Definition / answer" });
		front.setAttribute("aria-label", "Term or question");
		back.setAttribute("aria-label", "Definition or answer");
		const addBtn = btn("Add", null, "rv-tbtn");
		addBtn.type = "submit"; // btn() makes plain buttons; this one has to submit the form
		add.append(front, back, addBtn);
		add.addEventListener("submit", (e) => {
			e.preventDefault();
			const f = front.value.trim(), b = back.value.trim();
			if (!f || !b) return;
			active.cards.push({ id: uid(), front: f, back: b });
			save(); render(); announce("Card added. " + active.cards.length + (active.cards.length === 1 ? " card" : " cards") + " in this deck.");
			refocus(".rv-fc-add input");
		});
		main.appendChild(add);

		// bulk import (collapsible)
		const bulkWrap = el("details", "rv-fc-bulk");
		if (importOpts.open) bulkWrap.open = true;
		bulkWrap.addEventListener("toggle", () => { importOpts.open = bulkWrap.open; });
		const sum = el("summary", null, "Paste a list to import");
		const preset = sel(PRESETS.map((p) => [p.id, p.label]), importOpts.preset);
		preset.classList.add("rv-fc-preset");
		const presetField = field("Format", preset);
		const qTerm = sel([["tab", "Tab"], ["comma", "Comma"], ["custom", "Custom"]], importOpts.term);
		const qTermCustom = input("text", { value: importOpts.termCustom, placeholder: "e.g. ::" });
		qTermCustom.setAttribute("aria-label", "Custom text between term and definition");
		const qRow = sel([["newline", "New line"], ["semicolon", "Semicolon"], ["custom", "Custom"]], importOpts.row);
		const qRowCustom = input("text", { value: importOpts.rowCustom, placeholder: "e.g. ##" });
		qRowCustom.setAttribute("aria-label", "Custom text between rows");
		const termBox = el("div", "rv-fc-sep"); termBox.append(field("Between term and definition", qTerm), qTermCustom);
		const rowBox = el("div", "rv-fc-sep"); rowBox.append(field("Between rows", qRow), qRowCustom);
		const qOpts = el("div", "rv-fc-qopts"); qOpts.append(termBox, rowBox);
		const help = el("div", "rv-fc-bulk-help");
		help.id = "rv-fc-bulk-help";
		const bulkTa = ta();
		bulkTa.classList.add("rv-fc-bulk-ta");
		bulkTa.setAttribute("aria-label", "Cards to import");
		bulkTa.setAttribute("aria-describedby", help.id);
		bulkTa.spellcheck = false;
		const found = el("div", "rv-fc-bulk-found");
		found.setAttribute("aria-live", "polite");
		const opts = () => ({ term: importOpts.term, termCustom: importOpts.termCustom, row: importOpts.row, rowCustom: importOpts.rowCustom });
		// The count is a preview: it waits for a pause in typing and only reads the start of
		// a very large paste. Import below always parses the whole text.
		let previewTimer = 0;
		const preview = () => {
			clearTimeout(previewTimer);
			const text = bulkTa.value;
			if (!text.trim()) { found.textContent = ""; return; }
			if (text.length < 2048 && /^\s*(https?:\/\/|www\.)\S*\s*$/i.test(text)) { found.textContent = "That looks like a link. Paste the exported text instead."; return; }
			const { count: n, partial } = previewImport(text, importOpts.preset, opts());
			if (partial) found.textContent = n ? "At least " + n.toLocaleString() + (n === 1 ? " card" : " cards") + " found. Import reads the whole list." : "No cards found in the first part yet";
			else found.textContent = n ? n + (n === 1 ? " card found" : " cards found") : "No cards found yet";
		};
		const previewSoon = () => { clearTimeout(previewTimer); previewTimer = setTimeout(preview, 150); };
		const sync = () => {
			const p = PRESETS.find((x) => x.id === importOpts.preset) || PRESETS[0];
			help.textContent = "";
			p.help.forEach((line) => help.appendChild(el("p", null, line)));
			bulkTa.placeholder = p.sample;
			qOpts.hidden = p.id !== "quizlet";
			qTermCustom.hidden = importOpts.term !== "custom";
			qRowCustom.hidden = importOpts.row !== "custom";
			preview();
		};
		preset.addEventListener("change", () => { importOpts.preset = preset.value; sync(); });
		// Picking Custom shows the text box right after the select, next in Tab order. Focus
		// stays on the select: arrow keys change a closed select, so moving focus here would
		// pull a keyboard user out of it while they browse the options.
		qTerm.addEventListener("change", () => { importOpts.term = qTerm.value; sync(); });
		qRow.addEventListener("change", () => { importOpts.row = qRow.value; sync(); });
		qTermCustom.addEventListener("input", () => { importOpts.termCustom = qTermCustom.value; previewSoon(); });
		qRowCustom.addEventListener("input", () => { importOpts.rowCustom = qRowCustom.value; previewSoon(); });
		bulkTa.addEventListener("input", previewSoon);
		const bulkBtn = btn("Import cards", () => {
			clearTimeout(previewTimer);
			const cards = parseImport(bulkTa.value, importOpts.preset, opts());
			if (!cards.length) { bulkBtn.textContent = "No cards found"; setTimeout(() => (bulkBtn.textContent = "Import cards"), 1400); return; }
			active.cards.push(...cards); importOpts.open = false; save(); render();
			announce(cards.length + (cards.length === 1 ? " card" : " cards") + " imported");
			refocus(".rv-fc-add input");
		}, "rv-tbtn rv-tbtn-accent");
		const actions = el("div", "rv-fc-bulk-actions"); actions.append(bulkBtn, found);
		bulkWrap.append(sum, presetField, qOpts, help, bulkTa, actions);
		sync();
		main.appendChild(bulkWrap);

		// card list
		const listHead = el("div", "rv-fc-list-head");
		listHead.append(el("span", null, active.cards.length + (active.cards.length === 1 ? " card" : " cards")));
		if (active.cards.length) listHead.append(btn("Clear all", () => { if (confirm("Remove all cards from this deck?")) { active.cards = []; save(); render(); refocus(".rv-fc-add input"); } }, "rv-tbtn rv-tbtn-danger"));
		main.appendChild(listHead);

		const list = el("div", "rv-fc-list");
		active.cards.forEach((c) => {
			const rowEl = el("div", "rv-fc-card-row");
			rowEl.append(el("span", "rv-fc-card-front", c.front), el("span", "rv-fc-card-sep", "→"), el("span", "rv-fc-card-back", c.back));
			const rm = btn("✕", () => {
				const at = active.cards.findIndex((x) => x.id === c.id);
				active.cards = active.cards.filter((x) => x.id !== c.id); save(); render();
				announce("Card deleted");
				const left = root.querySelectorAll(".rv-fc-card-del");
				if (left.length) left[Math.min(at, left.length - 1)].focus(); else refocus(".rv-fc-add input");
			}, "rv-fc-card-del");
			rm.setAttribute("aria-label", "Delete card: " + c.front);
			rowEl.append(rm);
			list.appendChild(rowEl);
		});
		main.appendChild(list);

		s.append(aside, main);
	}

	function newDeck() {
		const d = { id: uid(), name: "Untitled deck", cards: [] };
		db.decks.unshift(d); db.active = d.id; save(); render();
		const t = refocus(".rv-fc-title"); // name it right away
		if (t) t.select();
	}

	// --- study mode ----------------------------------------------------------
	function renderStudy(deck) {
		const s = wrap(root); s.classList.add("rv-fc-study");
		// A fresh shuffled order + per-session "known" set each time study starts.
		let order = deck.cards.map((_, i) => i);
		for (let i = order.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [order[i], order[j]] = [order[j], order[i]]; }
		let pos = 0, flipped = false;
		const known = new Set();
		const counted = new Set(); // each card counts once per study session in the ledger
		// Reviewed (either answer) also counts once per card: 5 of them fill a school-day streak day.
		const reviewed = new Set();
		const review = (c) => { if (c && !reviewed.has(c.id)) { reviewed.add(c.id); bump("fr"); } };

		const bar = el("div", "rv-fc-study-bar");
		const exit = btn("← Decks", () => { deck._studying = false; render(); refocus(".rv-fc-bar .rv-tbtn-accent"); }, "rv-tbtn");
		const name = el("span", "rv-fc-study-name", deck.name);
		const shuffle = btn("Shuffle", () => { for (let i = order.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [order[i], order[j]] = [order[j], order[i]]; } pos = 0; flipped = false; paint(); }, "rv-tbtn");
		const prog = el("span", "rv-fc-study-prog");
		prog.setAttribute("role", "status");
		bar.append(exit, name, prog, shuffle);

		const card = el("div", "rv-fc-flip"); card.setAttribute("role", "button"); card.tabIndex = 0;
		const inner = el("div", "rv-fc-flip-inner");
		const faceF = el("div", "rv-fc-face rv-fc-face-front");
		const faceB = el("div", "rv-fc-face rv-fc-face-back");
		inner.append(faceF, faceB); card.appendChild(inner);
		const hint = el("div", "rv-fc-flip-hint", "Click the card or press Space to flip");
		hint.id = "rv-fc-hint";
		card.setAttribute("aria-describedby", hint.id);

		const nav = el("div", "rv-fc-study-nav");
		const prev = btn("‹ Prev", () => step(-1), "rv-tbtn");
		const gotIt = btn("✓ Got it", () => { const c = deck.cards[order[pos]]; review(c); if (c && !counted.has(c.id)) { counted.add(c.id); bump("fc"); } known.add(order[pos]); step(1); }, "rv-tbtn rv-tbtn-accent");
		const again = btn("↻ Study again", () => { review(deck.cards[order[pos]]); known.delete(order[pos]); step(1); }, "rv-tbtn");
		const next = btn("Next ›", () => step(1), "rv-tbtn");
		nav.append(prev, again, gotIt, next);

		const done = el("div", "rv-fc-done"); done.hidden = true;

		function paint() {
			const total = order.length;
			if (pos >= total) {
				card.hidden = true; hint.hidden = true; nav.hidden = true;
				done.hidden = false; done.textContent = "";
				const kc = known.size;
				done.append(el("div", "rv-fc-done-title", "Session complete"),
					el("div", "rv-fc-done-sub", "You marked " + kc + " of " + total + " as known."),
					btn("Study again", () => { pos = 0; flipped = false; known.clear(); card.hidden = false; hint.hidden = false; nav.hidden = false; done.hidden = true; paint(); card.focus(); }, "rv-tbtn rv-tbtn-accent"));
				prog.textContent = total + " / " + total;
				// The buttons that were focused are now hidden; land on the summary's button.
				announce("Session complete. You marked " + kc + " of " + total + " as known.");
				const again = done.querySelector("button");
				if (again && (nav.contains(document.activeElement) || card === document.activeElement || document.activeElement === document.body)) again.focus();
				return;
			}
			const c = deck.cards[order[pos]];
			faceF.textContent = c.front; faceB.textContent = c.back;
			card.classList.toggle("flipped", flipped);
			showFace();
			prog.textContent = (pos + 1) + " / " + total + (known.size ? "  ·  " + known.size + " known" : "");
			prev.disabled = pos === 0;
		}
		function step(dir) { pos += dir; flipped = false; if (pos < 0) pos = 0; paint(); }
		// Only the face that is showing is in the accessibility tree, so the answer isn't read early.
		function showFace() {
			faceF.setAttribute("aria-hidden", flipped ? "true" : "false");
			faceB.setAttribute("aria-hidden", flipped ? "false" : "true");
			card.setAttribute("aria-label", (flipped ? "Answer: " + faceB.textContent : "Question: " + faceF.textContent));
		}
		function flip() {
			flipped = !flipped; card.classList.toggle("flipped", flipped); showFace();
			if (flipped) announce("Answer: " + faceB.textContent);
		}

		card.addEventListener("click", flip);
		card.addEventListener("keydown", (e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); e.stopPropagation(); flip(); } });
		// Keyboard drive for study mode (arrows + space), scoped to this session.
		const onKey = (e) => {
			if (!document.body.contains(card)) { window.removeEventListener("keydown", onKey); return; }
			if (e.target && /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)) return;
			// Space on a focused button presses that button; it only flips from the card or the page.
			if (e.key === " " && e.target && e.target !== card && (e.target.tagName === "BUTTON" || e.target.getAttribute("role") === "button")) return;
			if (e.key === "ArrowRight") { e.preventDefault(); step(1); }
			else if (e.key === "ArrowLeft") { e.preventDefault(); step(-1); }
			else if (e.key === " ") { e.preventDefault(); flip(); }
		};
		window.addEventListener("keydown", onKey);

		s.append(flag, bar, card, hint, nav, done);
		paint();
		card.focus();
	}

	render();
}
